import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, AlertTriangle, Lock, Settings, Bell } from 'lucide-react';

interface NotificationBlockedModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function NotificationBlockedModal({ isOpen, onClose }: NotificationBlockedModalProps) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  if (!mounted) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[400] flex items-center justify-center p-4 h-[100dvh] w-full pointer-events-auto">
          {/* Backdrop with elegant blur */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
          />

          {/* Modal Container */}
          <div className="w-full max-w-md flex flex-col max-h-[90vh] pointer-events-auto z-10">
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              className="relative w-full h-full bg-[#0f172a] border border-white/10 rounded-3xl shadow-2xl overflow-hidden flex flex-col theme-font-primary"
            >
            {/* Ambient upper glow */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 bg-red-500/10 blur-[50px] rounded-full pointer-events-none" />

            {/* Header / Top Bar */}
            <div className="p-5 pb-3 flex justify-between items-start border-b border-white/5 relative z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-400 shadow-[0_0_15px_rgba(239,68,68,0.15)] animate-pulse">
                  <AlertTriangle size={20} />
                </div>
                <div>
                  <h3 className="text-sm font-black uppercase tracking-widest text-white italic">
                    Acceso Bloqueado
                  </h3>
                  <p className="text-[10px] text-white/40 font-medium">
                    Permiso de Notificaciones denegado
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 hover:bg-white/10 rounded-xl transition-colors text-white/50 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body Content */}
            <div className="p-6 space-y-5 overflow-y-auto custom-scrollbar relative z-10">
              <p className="text-[11px] leading-relaxed text-slate-300">
                Tu navegador tiene las <strong className="text-white">Notificaciones Deshabilitadas</strong> o bloqueadas para este sitio. Por este motivo, el sistema no puede enviarte avisos de torneos ni alertas de partidos en tiempo real.
              </p>

              {/* Instructions Box */}
              <div className="space-y-4">
                <h4 className="text-[10px] font-black uppercase tracking-widest theme-primary-text italic">
                  ¿Cómo habilitarlas en tu dispositivo?
                </h4>

                <div className="space-y-3">
                  {/* Step 1 */}
                  <div className="flex gap-3 p-3 bg-white/[0.02] border border-white/5 rounded-2xl group hover:border-white/10 transition-colors">
                    <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-slate-400 shrink-0">
                      <Lock size={14} />
                    </div>
                    <div className="space-y-0.5">
                      <p className="text-[10px] font-black uppercase text-white">1. Abre el candado del sitio</p>
                      <p className="text-[9px] text-slate-400 leading-relaxed">
                        Haz clic o toca el ícono del <strong className="text-white">candado 🔒</strong> o el botón de configuración situado a la izquierda de la dirección URL en la barra de navegación.
                      </p>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="flex gap-3 p-3 bg-white/[0.02] border border-white/5 rounded-2xl group hover:border-white/10 transition-colors">
                    <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-slate-400 shrink-0">
                      <Settings size={14} />
                    </div>
                    <div className="space-y-0.5">
                      <p className="text-[10px] font-black uppercase text-white">2. Activa el Permiso</p>
                      <p className="text-[9px] text-slate-400 leading-relaxed">
                        Busca la option de <strong className="text-white">"Notificaciones"</strong> y cámbiala a <strong className="text-emerald-400">"Permitir"</strong> (o "Allow").
                      </p>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="flex gap-3 p-3 bg-white/[0.02] border border-white/5 rounded-2xl group hover:border-white/10 transition-colors">
                    <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-slate-400 shrink-0">
                      <Bell size={14} />
                    </div>
                    <div className="space-y-0.5">
                      <p className="text-[10px] font-black uppercase text-white">3. Recarga y activa</p>
                      <p className="text-[9px] text-slate-400 leading-relaxed">
                        Recarga la página para aplicar los cambios y vuelve a pulsar el botón de notificaciones para activarlas de forma definitiva.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* iOS / Safari Banner */}
              <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-2xl text-[9px] text-blue-300 leading-relaxed">
                💡 <strong className="text-white">¿Estás en iPhone / Safari?</strong> Ve a <strong className="text-white">Ajustes</strong> de tu iPhone &gt; <strong className="text-white">Notificaciones &gt; RPT</strong> y asegúrate de que estén permitidas. Si no lo has instalado aún, recuerda guardarlo primero en tu pantalla de inicio como App.
              </div>

              {/* Reset Permissions Hint */}
              <div className="p-3 bg-white/[0.03] border border-white/10 rounded-2xl text-[9px] text-slate-300 leading-relaxed">
                <strong className="text-white">¿Aún sigue sin funcionar?</strong> Haz click en el candado 🔒 de la URL, selecciona <strong className="text-white font-bold">"Restablecer permisos"</strong> en el navegador y recarga la página.
              </div>
            </div>

            {/* Footer Actions */}
            <div className="p-5 pt-0 relative z-10 flex gap-2">
              <button
                onClick={onClose}
                className="w-full theme-primary-bg text-black font-black py-3 rounded-2xl uppercase tracking-[0.15em] text-[10px] shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-1.5 cursor-pointer"
              >
                Entendido
              </button>
            </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
}
